/**
 * VARIANTS — WHAT MASTERY BUYS. THE HALF OF SECTION 1.5l(a) THAT DID NOT EXIST.
 *
 * OWNER RULING, CONTRACT.md section 1.5l(a), 2026-09-02: "MASTERY DEEPENS THE
 * SPECIES IT IS SPENT ON. Mastering mackerel changes mackerel: LARGER AND RARER
 * VARIANTS BEGIN APPEARING, the ledger records a personal best, and FORMS EXIST
 * THAT A LOW-MASTERY PLAYER NEVER SEES. The reward is that the thing you chose
 * becomes richer, so the choice compounds instead of only costing."
 *
 * The ledger half landed in round 12 as `SimState.records`. This file is the
 * other half. Two readings were rejected on the record and neither is built
 * here: mastery as a KEY that unlocks places and names, and mastery as a BARE
 * RECORD with no effect. A variant is neither. It is a form of the fish you
 * already fish for, and the only thing it changes is what you land.
 *
 * ---------------------------------------------------------------------------
 * WHY A VARIANT CAN ONLY BE A WEIGHT. THIS IS FORCED, NOT CHOSEN.
 * ---------------------------------------------------------------------------
 *
 * CONTRACT.md section 6 property 2: "Targeting changes composition, not
 * throughput. A targeted run and an untargeted run over the same seed land
 * within the same catch budget; what differs is the distribution across
 * species." `targeting.test.ts` asserts that with ZERO tolerance on five axes —
 * landings, xp, coins, specials, junk — and mastery accumulates only under a
 * target, so ANY reward that moved one of those five would break the equality
 * on the first tick it applied.
 *
 * `step.ts` already splits the engine into two roles for exactly this reason:
 *
 *   PACE   — `bestUnlockedSpecies(level)`. Cast length, junk odds, special
 *            odds, xp, coins. A function of LEVEL alone. Nothing here may
 *            reach it, and nothing here does.
 *   QUARRY — `resolveSpecies(state, level)`. WHICH fish, and therefore what it
 *            WEIGHS and which key it lands on.
 *
 * A mastery reward may only live on the quarry side, and the quarry side has
 * exactly two observable quantities: the species key and the weight. The key is
 * the player's choice already. So the weight is the whole available surface, and
 * a variant is a scaled weight with a name on it. That is not a modest reading
 * of the ruling; it is the only reading the properties leave open, and the
 * ruling names it first: LARGER and rarer.
 *
 * THE BAR HAS THE REWARD THIS ENGINE CANNOT COPY, and it is worth naming so the
 * omission is not read as an oversight. Melvor's fishing mastery 65 removes junk
 * for that fish (`gauntlet/refs/bar-depth.json`, MEASURED from the wiki's raw
 * wikitext). Junk is a THROUGHPUT axis here. That exact reward is one property 2
 * forbids, and `mastery.ts` said so a round before this file existed.
 *
 * ---------------------------------------------------------------------------
 * WHAT MOVES AND WHAT DOES NOT, STATED AS ARITHMETIC
 * ---------------------------------------------------------------------------
 *
 * A variant multiplies ONE cast's weight draw. It therefore moves:
 *   - `weightG`, the running total, which is a COMPOSITION axis and already
 *     moves with the target (targeting.test.ts asserts that it MUST);
 *   - `records[id]`, the personal best, which is a maximum over those draws;
 *   - `variants`, the tally in this file's own shape.
 *
 * It moves NOTHING else. Not the cast length, not the outcome roll, not the xp,
 * not the coins, not the catch count. A player with 60 hours in mackerel and a
 * player with none land the SAME NUMBER OF MACKEREL IN THE SAME HOUR and are
 * paid the same coins for them. What differs is that some of the mastered
 * player's mackerel are Elder Mackerel.
 *
 * THE SIZE OF THE AGGREGATE EFFECT, MODELLED from the table below and MEASURED
 * by `node tools/lanes/progression/variants.mjs`: with all three rungs open, the
 * expected weight multiplier is 1 + 0.050*0.25 + 0.010*0.50 + 0.001*1.00 =
 * 1.0185. Under two per cent on total landed weight, at the very top of the
 * ladder. That number is deliberately small: the reward is the individual fish
 * and the number in the ledger, not a hidden 2% earnings bonus, and a small
 * aggregate is what keeps the dominance probe measuring the species table rather
 * than measuring this file.
 *
 * ---------------------------------------------------------------------------
 * EARNED, NEVER PURCHASABLE — THE SHARPEST EDGE ON THE RULING
 * ---------------------------------------------------------------------------
 *
 * 1.5l(a), binding on the build: "whatever mastery unlocks is EARNED, NEVER
 * PURCHASABLE. It is inside the pay-to-win guarantee's protected set as of round
 * 1, and a paid mastery multiplier was measured taking rule C to 6,153 failures
 * of 78,290. A VARIANT GATED ON MASTERY IS GATED ON HOURS."
 *
 * THIS FILE ADDS NO NEW SURFACE A PURCHASE COULD REACH, and the reason is
 * structural rather than careful. `variantFor` takes two numbers: a mastery
 * total and a roll. The roll is `randBelow(seed, tick, CHANNEL.VARIANT, 1000)` —
 * addressed by tick, like every other draw in this engine, with no cursor and no
 * state. The mastery total is `state.mastery[quarry.id]`, which `mastery.ts`
 * derives to be ONE TICK OF A CAST THAT LANDED THAT SPECIES and which nothing
 * outside the rate engine writes. There is no third argument, so there is
 * nowhere for an entitlement to enter; `src/sim/entitlements.test.ts` walks the
 * rate engine's import closure and this module is inside it.
 *
 * The remaining route is the one the guarantee actually measures: a layer that
 * holds both a SimState and a Wardrobe — `persistence.ts` — inflating the
 * mastery total before the engine reads it. MEASURED against that, by planting
 * an accelerator that names `variantFor` and reaches it through
 * `envelope.wardrobe.owned.length`: `tools/instruments/paywin.mjs` rule C goes
 * from 0 failures of 80,210 to a four-figure count. The figure is in this
 * round's report; the plant type-checks cleanly, which is the point of it.
 *
 * ---------------------------------------------------------------------------
 * WHERE THE NUMBERS COME FROM. DERIVED, GUESSED, AND WHICH IS WHICH.
 * ---------------------------------------------------------------------------
 *
 * `MASTERY_FULL` is the round's one unruled guess and the owner has been told
 * so. Everything about WHEN a variant appears is therefore expressed as a
 * multiple of it and never as a number of its own, so moving that one constant
 * moves this whole ladder with it and nothing here needs re-tuning beside it.
 *
 *   DERIVED FROM MASTERY_FULL — the three thresholds:
 *     Fine   MASTERY_FULL / 3   Prize  MASTERY_FULL   Elder  MASTERY_FULL * 3
 *   At the current MASTERY_FULL that reads 6.7 h, 20 h and 60 h of landing
 *   casts. The middle rung IS the ruled meaning of "mastered" — 1.5l(a) says
 *   mastering mackerel changes mackerel, so the rung that matters most sits
 *   exactly on the word.
 *
 *   NEW GUESS 1 — THE RUNG COUNT, three. One below mastery so the reward starts
 *   before the milestone rather than arriving as a cliff; one on it; one past it
 *   so a favourite species still has somewhere to go at hour sixty.
 *
 *   NEW GUESS 2 — THE THRESHOLD RATIO, 3. Geometric, centred on MASTERY_FULL.
 *   A ratio of 2 puts the top rung at 40 h and makes the ladder short; a ratio
 *   of 4 puts it at 80 h, which is 11% of the 742-hour climb to level 99 spent
 *   on one fish. Three is the middle and it is a guess.
 *
 *   NEW GUESS 3 — THE RARITIES, 50, 10 and 1 per thousand landings. Geometric
 *   with ratio 10: each rung is ten times rarer than the one below it. The
 *   PER-THOUSAND unit is DERIVED — `species.ts` already states junk and special
 *   odds in parts per thousand and this reads off the same 1000-space roll.
 *
 *   NEW GUESS 4 — THE SCALES, 5/4, 3/2 and 2/1. Small exact fractions so the
 *   arithmetic stays integral (see below). They are the reason a mastered
 *   mackerel is worth talking about: the base band tops out at 700 g, and an
 *   Elder Mackerel reaches 1,400 g. A newcomer's ledger cannot show that number.
 *
 * WHAT WOULD CHANGE IF THE OWNER MOVES MASTERY_FULL: every threshold, in
 * proportion, and nothing else. What would change if they reject a rarity or a
 * scale: one row of the table below. `tools/lanes/progression/variants.mjs`
 * prints the consequences at whatever these are set to, the same way
 * `pacing.mjs` does for MASTERY_FULL, so a ruling is taken against a measurement
 * rather than against a number.
 *
 * ---------------------------------------------------------------------------
 * MASTERY IS NOW READ BY THE ENGINE, AND THAT IS A REAL CHANGE
 * ---------------------------------------------------------------------------
 *
 * `mastery.ts` and `state.ts` both said, in round 11, that nothing on the rate
 * path reads mastery and that this was forced rather than chosen. Half of that
 * is still exactly true and half of it has moved, and pretending otherwise would
 * be the kind of stale docstring this lane has been bitten by twice:
 *
 *   STILL TRUE, AND STILL FORCED. Nothing on the TEMPO path reads mastery.
 *   `castTicksFor`, the outcome roll, `pace.xp` and `pace.coins` cannot see it,
 *   and property 2's zero-tolerance equality is what forbids it.
 *
 *   NO LONGER TRUE. The COMPOSITION path reads it, here, once, to decide which
 *   forms of the quarry are available. That is not a loophole in property 2; it
 *   is the reward 1.5l(a) rules must exist, landing on the only axis property 2
 *   leaves free.
 *
 * The consequence for the guess: MASTERY_FULL is LOAD-BEARING FROM THIS ROUND
 * ONWARD. Before this file, moving it changed what the word "mastered" meant and
 * moved no earned quantity. Now it also moves when a player's fish start getting
 * bigger. That is what 1.5l(a) asked for and it raises the stakes on the ruling
 * the owner still owes, so it is said here rather than discovered later.
 *
 * ---------------------------------------------------------------------------
 * INTEGER ARITHMETIC, BECAUSE THE STATE HAS NO FLOATS IN IT
 * ---------------------------------------------------------------------------
 *
 * `state.ts`: "Every accumulated quantity here is an INTEGER... Weight is stored
 * in grams, not kilograms, for exactly this reason." A scale is therefore a
 * NUMERATOR AND A DENOMINATOR and never a float. `Math.floor((w * num) / den)`
 * on a weight bounded by 551,000 g and a numerator bounded by 2 stays four
 * orders of magnitude inside safe-integer range, so the product is exact on
 * every engine and a live run and a settled run cannot drift apart in the last
 * bits. A `1.25` here would have put a float on the path that feeds `weightG`,
 * `records` and the offline invariant's zero tolerance.
 */

import { MASTERY_FULL } from './mastery';
import { isKnownSpecies } from './species';

/**
 * THE ROLL SPACE, in parts per thousand. DERIVED, not chosen: `species.ts`
 * already states junk and special odds per thousand and `stepTick` already draws
 * `randBelow(..., 1000)` for the outcome. A second denominator would be a second
 * place the odds can be misread.
 */
export const VARIANT_ROLL_SPACE = 1000;

/**
 * THE GEOMETRIC RATIO BETWEEN RUNGS, on the mastery axis. A NEW GUESS, isolated
 * here for the same reason MASTERY_FULL is isolated in mastery.ts: so a ruling
 * against it is one line.
 */
export const VARIANT_MASTERY_RATIO = 3;

/** The character that joins a species id to a variant id in the tally's keys. */
export const VARIANT_KEY_SEPARATOR = ':';

export interface Variant {
  /** Stable id. Appears in saves forever; never renamed, only added to. */
  readonly id: string;
  /** The word that goes in front of the species name. "Elder" + "Mackerel". */
  readonly name: string;
  /**
   * Mastery points in the SPECIES BEING LANDED at which this form starts
   * appearing. DERIVED from MASTERY_FULL; see the header.
   */
  readonly masteryAt: number;
  /** How often it comes up once it is available, in parts per VARIANT_ROLL_SPACE. */
  readonly per1000: number;
  /** The weight scale, as an exact fraction. Never a float; see the header. */
  readonly scaleNum: number;
  readonly scaleDen: number;
}

/**
 * THE LADDER, RAREST FIRST — and the order is load-bearing, not cosmetic.
 *
 * The bands below are laid out from 0 upward in this order, so the rarest form
 * owns the lowest slice of the roll space. That gives every rung a FIXED,
 * INDEPENDENT rate: unlocking Elder takes its 1-in-1000 out of the plain fish
 * and never out of Prize or Fine. P(Fine) is exactly 50/1000 at every mastery
 * from 24,000 points upward, forever, which is a sentence a fence can assert
 * with an exact count instead of a tolerance.
 *
 * The alternative — a cumulative ladder where a higher rung eats the one below —
 * makes every rate a function of every threshold, so a fourth rung would silently
 * re-tune the other three. This one does not.
 *
 * A NOTE ON THE NAMES, so it is not read as finished. These three words are
 * shared across all 24 species: an Elder Mackerel and an Elder Sunfish take the
 * same adjective. 1.5l(d) ruled that every ISLAND is hand-authored; nothing has
 * been ruled about whether every FORM is, and 24 species x 3 rungs is 72 names
 * that would be a content decision with an owner gate rather than a constant a
 * sim builder picks. The shared ladder satisfies "forms exist that a low-mastery
 * player never sees" today and is one table away from per-species naming if the
 * owner wants it.
 */
export const VARIANTS: readonly Variant[] = [
  {
    id: 'elder',
    name: 'Elder',
    masteryAt: MASTERY_FULL * VARIANT_MASTERY_RATIO,
    per1000: 1,
    scaleNum: 2,
    scaleDen: 1,
  },
  {
    id: 'prize',
    name: 'Prize',
    masteryAt: MASTERY_FULL,
    per1000: 10,
    scaleNum: 3,
    scaleDen: 2,
  },
  {
    id: 'fine',
    name: 'Fine',
    masteryAt: MASTERY_FULL / VARIANT_MASTERY_RATIO,
    per1000: 50,
    scaleNum: 5,
    scaleDen: 4,
  },
];

/**
 * The half-open roll band each rung owns, stacked from 0 in table order.
 * Computed once, from the table, so a rung cannot be given a band by hand.
 */
const BANDS: readonly { readonly variant: Variant; readonly end: number }[] = (() => {
  const out: { variant: Variant; end: number }[] = [];
  let end = 0;
  for (const v of VARIANTS) {
    end += v.per1000;
    out.push({ variant: v, end });
  }
  return out;
})();

/** Total slice of the roll space the ladder occupies. The rest is the plain fish. */
export const VARIANT_BAND_TOTAL = BANDS.length === 0 ? 0 : BANDS[BANDS.length - 1]!.end;

const BY_ID: ReadonlyMap<string, Variant> = new Map(VARIANTS.map((v) => [v.id, v]));

/** Lookup by id. Returns undefined for an id this build does not know. */
export function variantById(id: string): Variant | undefined {
  return BY_ID.get(id);
}

/**
 * WHICH FORM THIS CAST LANDED, OR `null` FOR THE PLAIN FISH.
 *
 * TWO NUMBERS IN, AND THAT IS THE WHOLE INTERFACE. `points` is the player's
 * mastery IN THE SPECIES BEING LANDED — 1.5j(d) rules that bycatch counts toward
 * the species landed, so the form of a bycaught mackerel is decided by mackerel
 * mastery whatever was being chased, and this function needs no edit when
 * bycatch lands because it is never told what the target was. `roll` is a uniform
 * integer in [0, VARIANT_ROLL_SPACE) drawn by the caller on its own channel.
 *
 * THERE IS NO THIRD ARGUMENT. That is the pay-to-win guarantee stated as a
 * signature: an entitlement has nowhere to enter, and a caller that wanted to
 * accelerate this would have to inflate the mastery total itself, which is a
 * value the guarantee's rule C already sweeps.
 *
 * A ROLL IN AN UNLOCKED RUNG'S BAND FALLS THROUGH TO THE PLAIN FISH rather than
 * to the rung below it. That is what makes each rate independent of every other
 * threshold; see the note on VARIANTS.
 */
export function variantFor(points: number, roll: number): Variant | null {
  if (!Number.isFinite(points) || !Number.isFinite(roll)) return null;
  for (const band of BANDS) {
    if (roll < band.end) return points >= band.variant.masteryAt ? band.variant : null;
  }
  return null;
}

/**
 * THE ONE PLACE A VARIANT TOUCHES A QUANTITY.
 *
 * Integer in, integer out, by an exact fraction. `Math.floor` and not `Math.round`
 * so the result is never above `weightG * scaleNum / scaleDen` — a rounding that
 * could exceed the fraction would put a record fractionally past what the table
 * says the form can produce, and the table is what a report cites.
 */
export function weighVariant(weightG: number, variant: Variant | null): number {
  if (variant === null) return weightG;
  if (!Number.isFinite(weightG)) return 0;
  return Math.floor((weightG * variant.scaleNum) / variant.scaleDen);
}

/**
 * THE TALLY KEY: species id, a colon, variant id. `mackerel:elder`.
 *
 * FLAT AND NOT NESTED, so `withTallyEntry` in mastery.ts sorts and inserts it the
 * same way it does `catches`, `mastery` and `records`, and `normaliseTally` in
 * save.ts loads it through the same door. A nested map would be a second shape
 * with a second key-order rule, and SIM-D2 was one key-order rule got wrong once.
 */
export function variantKey(speciesId: string, variantId: string): string {
  return `${speciesId}${VARIANT_KEY_SEPARATOR}${variantId}`;
}

/**
 * True if this build knows both halves of the key.
 *
 * The loader uses it for exactly one thing: RETENTION ORDER under the 512-key
 * cap, where a key this build knows is kept ahead of one it does not. An id from
 * a newer build is still KEPT and still normalised — dropping it would delete
 * earned progress on the way back, which CONTRACT.md section 6 forbids and which
 * save.ts's own docstring spends four paragraphs on.
 */
export function isKnownVariantKey(key: string): boolean {
  const at = key.indexOf(VARIANT_KEY_SEPARATOR);
  if (at <= 0 || at === key.length - 1) return false;
  return isKnownSpecies(key.slice(0, at)) && BY_ID.has(key.slice(at + 1));
}

/** Every form available to a player at this mastery, rarest first. */
export function unlockedVariants(points: number): readonly Variant[] {
  if (!Number.isFinite(points)) return [];
  return VARIANTS.filter((v) => points >= v.masteryAt);
}
